import { EntityType } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { useAccountRepository } from '@frontend/repository';
import { SettingClient, SettingTemplateClient } from '@frontend/setting/api';
import { CreateSetting, Setting, SettingTemplate } from '@frontend/setting/types';
import { ToastUtil } from '@frontend/toast-utils';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

interface ViewProps {
    entity_id?: string;
    entity_type?: string;
    isLoading: boolean;
    template: SettingTemplate[] | null;
    formValues: Record<string, CreateSetting>;
    setFormValues: React.Dispatch<React.SetStateAction<Record<string, CreateSetting>>>;
    onSubmit: () => Promise<void>;
    onCancel: () => void;
}

const useSettingCreate = (): ViewProps => {
    const navigate = useNavigate();
    const { currentAccount } = useAccountRepository();
    const { entity_type, entity_id } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [original, setOriginal] = useState<Setting[]>([]);
    const [formValues, setFormValues] = useState<Record<string, CreateSetting>>({});
    const [template, setTemplate] = useState<SettingTemplate[] | null>(null);

    useEffect(() => {
        if (entity_type && entity_id) {
            const type = entity_type.slice(0, -1) as EntityType;
            SettingClient.fetchSettings({ account_id: currentAccount, entity_type: type, entity_id })
                .then((settings) => {
                    setOriginal(settings.results);
                    const newFormValues: Record<string, CreateSetting> = {};
                    settings.results.forEach((setting) => {
                        newFormValues[setting.key] = {
                            value: setting.value,
                            entity_type: setting.entity_type,
                            entity_id: setting.entity_id,
                            key: setting.key
                        };
                    });
                    setFormValues(newFormValues);
                })
                .finally(() => {
                    SettingTemplateClient.fetchSettingTemplates(Object.fromEntries(new URLSearchParams(window.location.search).entries()))
                        .then((templates) => {
                            if (type in templates) setTemplate(templates[type]);
                        })
                        .finally(() => setIsLoading(false));
                });
        } else setIsLoading(false);
    }, [entity_type, entity_id]);

    const onSubmit = async () => {
        const keys = Object.keys(formValues);
        if (keys.length === 0 || entity_id === undefined || entity_type === undefined) return;
        const type = entity_type.slice(0, -1) as EntityType;
        const settings = keys
            .map((key) => {
                const found = original.find((s) => s.key === key);
                let value = formValues[key].value;
                try {
                    if (formValues[key].value && typeof formValues[key].value === 'string' && Array.isArray(JSON.parse(formValues[key].value))) {
                        value = (JSON.parse(formValues[key].value) as any[]).map((v) => v.value);
                    }
                } catch {}
                if (found) {
                    // Only patch if value has changed
                    const originalValue = JSON.stringify(found.value);
                    const newValue = JSON.stringify(value);
                    if (originalValue === newValue) {
                        return null; // Skip unchanged settings
                    }
                    return SettingClient.patchSetting(currentAccount, found.id, {
                        value: value
                    }).catch((err) => {
                        throw {
                            setting: key
                        };
                    });
                } else
                    return SettingClient.postSetting(currentAccount, {
                        value: value,
                        entity_type: type,
                        entity_id: entity_id,
                        key: key
                    }).catch((err) => {
                        throw {
                            setting: key
                        };
                    });
            })
            .filter((promise) => promise !== null);
        if (settings.length === 0) {
            navigate(-1);
            return;
        }
        const promises = await Promise.allSettled(settings);
        const rejected = promises.filter((result) => result.status === 'rejected');
        if (rejected.length > 0) {
            const errorMessages = rejected.map((result) => (result as PromiseRejectedResult).reason);
            ToastUtil.error(
                CommonMessage.TOAST.PROMISE.ERROR,
                <div className='d-flex flex-column'>
                    <span>
                        <FormattedMessage
                            id='setting.create.error'
                            defaultMessage='An error occured while creating the following settings'
                        />
                    </span>
                    {errorMessages.map((err) => (
                        <span>{err.setting.replace(/_/g, ' ')}</span>
                    ))}
                </div>
            );
            return;
        } else {
            ToastUtil.success(CommonMessage.TOAST.PROMISE.SUCCESS, CommonMessage.TOAST.CREATE.SUCCESS(CommonMessage.OBJECTS.SETTING.PLURAL));
        }
        navigate(-1);
    };

    const onCancel = () => {
        navigate(-1);
    };

    return {
        entity_id,
        entity_type: entity_type?.slice(0, -1) as EntityType | undefined,
        isLoading,
        template,
        formValues,
        setFormValues,
        onSubmit,
        onCancel
    };
};

export default useSettingCreate;
